(ns dashboard.entity.question.comparison
  "The principle contract of the dashboard.entity component namespaces are this:
   1. All components will NOT depend on keyword references to subs and events in
      external NSes, but rather receive data as arguments, functions and callbacks.
   2. All internal reliance on the re-frame app DB must be internally contained.
   3. All re-frame app subs and events must NOT be directly referenced by outside
      NSes, but rather via GETTER and SETTER functions - the same is true of internal
      calls - prefer using parameterized functions.
   4. The entity is a UI component that may encapsulate low-level business rules on
      UI controls, but is not responsible for hydrating components with data or entrenched
      references to component namespaces. The UI is customizable, and top-level entities
      should have configurable and interchangable UI elements.

   This namespace is legacy code from the comparisons feature."
  (:require [clojure.string :as str]
            [dashboard.components.panel :as panel]
            [dashboard.entity.question.db :as questions.db]
            [dashboard.services.questionnaire-list :as questionnaire-list]
            [re-frame.core :as rf]))

(def symbol->background
  {"T" "#209CEE"
   "Q" "black"
   "A" "purple"})

(defn ComparisonSymbol [short-symbol short-id]
  [:span.mr-1.has-text-white.has-text-weight-bold
   {:style {:display "inline-block"
            :border-radius "50%"
            :background (symbol->background short-symbol)
            :width "1.7em"
            :height "1.7em"
            :line-height "1.7em"
            :text-align "center"}}
   (str short-symbol short-id)])

(defn Compare [questionnaire-id idx {:keys [target-questionnaire] :as comparison}]
  (let [{:keys [loading
                load-success]} @(questions.db/comparison-suggestions*
                                 questionnaire-id
                                 (:id target-questionnaire))]
    [:div.is-flex.is-align-items-center
     [:label
      {:for (str "template-" idx)}
      [:span "Compare with"]]
     [:div.select.is-rounded.ml-2
      {:class (cond
                loading
                "is-loading"

                (and (seq comparison)
                     (not loading)
                     (not load-success))
                "is-danger")}
      [:select
       {:id (str "template-" idx)
        :value (if-let [{:keys [id template]} target-questionnaire]
                 (if template
                   (str "template," id)
                   (str "questionnaire," id))
                 "clear-selection,")
        :on-change #(let [[option-type,value] (str/split (.. % -target -value) #",")]
                      (if (= "clear-selection" option-type)
                        (questions.db/clear-comparison-target questionnaire-id
                                                              target-questionnaire)
                        (let [target-questionnaire-id (parse-uuid value)]
                          (questions.db/get-suggestions idx
                                                        questionnaire-id
                                                        target-questionnaire-id))))}
       [:option
        {:value "clear-selection,"}
        ""]
       (for [{:keys [id custom-name filename]} @(rf/subscribe [::questionnaire-list/data :template])]
         [:option
          {:key id
           :value (str "template," id)}
          (str "Template: " (or custom-name filename))])
       (for [{:keys [id custom-name filename]} @(rf/subscribe [::questionnaire-list/data (fn [questionnaire]
                                                                                           (and (not (:template questionnaire))
                                                                                                (not= questionnaire-id (:id questionnaire))))])]
         [:option
          {:key id
           :value (str "questionnaire," id)}
          (str "Questionnaire: " (or custom-name filename))])]]]))

(defn ComparisonControl [questionnaire-id questions]
  (let [visible? @(questions.db/comparison-control-visible* questionnaire-id)
        comparisons @(questions.db/comparisons* questionnaire-id)
        applied-analyses-linked? (->> questions
                                      (filter #(seq (-> % :selected-answer :applied-analyses)))
                                      seq
                                      boolean)
        analysis-suggestions-visible @(questions.db/analysis-suggestions-visible* questionnaire-id)
        analysis-suggestions-loading @(questions.db/analysis-suggestions-loading* questionnaire-id)]
    [panel/panel
     {:title "Comparison control"
      :class ["is-tertiary mb-0 has-no-shadow"]
      :panel-class :has-background-highlight
      :actions [:div
                [:button.button.is-small.is-rounded.ml-2.is-secondary
                 {:on-click #(questions.db/toggle-comparison-control-visible questionnaire-id)
                  :title "Open controls to compare questionnaire with other context"}
                 [:span.icon>i.fas
                  {:class (if visible?
                            "fa-chevron-up"
                            "fa-chevron-down")}]]]}
     (when visible?
       [:div.is-flex.is-flex-direction-row.is-align-items-center.is-justify-content-flex-end
        [:div.is-flex.is-flex-direction-column.is-align-items-end
         [:div.is-flex.is-align-items-center.mt-2
          (when analysis-suggestions-loading
            [:div.loader.mr-2])
          [:label.checkbox.mr-2
           [:input
            {:type "checkbox"
             :checked analysis-suggestions-visible
             :on-change #(if analysis-suggestions-visible
                           (questions.db/hide-analysis-suggestions questionnaire-id)
                           (questions.db/get-analysis-suggestions questionnaire-id))}]
           [:span.ml-1 "Show analysis suggestions for questions"]]
          [ComparisonSymbol "A" nil]]
         (for [[idx comparison] (map-indexed vector comparisons)]
           [:div.is-flex.is-align-items-center.mt-2.mr-2
            {:key idx}
            [:f> Compare questionnaire-id idx comparison]
            (when-let [target-questionnaire (:target-questionnaire comparison)]
              (let [{:keys [matching-question-count question-count]} (questions.db/matching-question-count questions (:id target-questionnaire))]
                [:div.ml-2
                 [ComparisonSymbol
                  (if (:template target-questionnaire) "T" "Q")
                  (:short-id comparison)]
                 [:span
                  {:title (str matching-question-count " out of " question-count " questions match the template")}
                  (str (.toFixed (* 100 (/ (float matching-question-count) (float question-count))) 0) "%")]
                 (when (:template target-questionnaire)
                   (let [my-question-id->template-question-id (questions.db/question-matches-with-local-analyses (:id target-questionnaire) questions)]
                     [:button.button.is-small.is-rounded.is-info.ml-2
                      {:title (cond
                                (not applied-analyses-linked?)
                                "Link applied analyses to the selected answer of questions, to be able to add the structured analyses to the template"

                                (empty? my-question-id->template-question-id)
                                "No matching questions in the template, into which to add our analyses"

                                :else (str "Add structured analyses from the matching question into the template, on the master account."
                                           "\n\n"
                                           "Will add analyses from " (count my-question-id->template-question-id) " questions"))
                       :disabled (or (not applied-analyses-linked?)
                                     (empty? my-question-id->template-question-id))
                       :on-click #(questions.db/push-linked-structured-analyses target-questionnaire questions)}
                      "Add analysis"]))]))
            (when-not (zero? idx)
              [:button.button.is-rounded.is-small.is-danger.ml-2
               {:title "Remove this comparison item"
                :on-click #(questions.db/remove-comparison questionnaire-id idx)}
               [:span.icon>i.fas.fa-times-circle]])])
         [:button.button.is-rounded.is-primary.m-2
          {:title "Adds a new comparison item"
           :on-click #(questions.db/add-new-comparison questionnaire-id)}
          [:span.icon>i.fas.fa-plus]
          [:span "Add new comparison"]]]])]))